import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ResolverService {
  apiURL: any;
  constructor(private http: HttpClient) {
    this.apiURL = environment.apiURL;
  }
  private sidebarOpenSubject = new BehaviorSubject<boolean>(true);
  sidebarOpen$ = this.sidebarOpenSubject.asObservable();

  toggleSidebar() {
    this.sidebarOpenSubject.next(!this.sidebarOpenSubject.value);
  }

  setSidebarState(isOpen: boolean) {
    this.sidebarOpenSubject.next(isOpen);
  }
  
  public getClientFeatureConfig(client_id: any) {
    return this.http.get(`${this.apiURL}/api/clients/${client_id}/feature-config?client_id=${client_id}`);
  }

  public updateClientFeatureConfig(client_id: any, data: any) {
    return this.http.put(`${this.apiURL}/api/clients/${client_id}/feature-config?client_id=${client_id}`, data);
  }

  // public getFlows(client_id: any, active_only?: boolean) {
  //   return this.http.get(`${this.apiURL}/api/flows?client_id=${client_id}` + (active_only ? '&active_only=true' : ''));
  // }
  public getFlows(client_id: any, active_only?: boolean) {
    return this.http.get(`${this.apiURL}/api/flows?client_id=${client_id}&active_only=true`);
  }
  
  public getFlowById(client_id: any, id: any) {
    return this.http.get(`${this.apiURL}/api/flows/${id}?client_id=${client_id}`);
  }

  public updateFlow(client_id: any, id: any, data: any) {
    return this.http.put(`${this.apiURL}/api/flows/${id}?client_id=${client_id}`, data);
  }

  public createFlow(client_id: any, data: any) {
    return this.http.post(`${this.apiURL}/api/flows?client_id=${client_id}`, data);
  }

  public deleteFlow(client_id: any, id: any) {
    return this.http.delete(`${this.apiURL}/api/flows/${id}?client_id=${client_id}`);
  }

  public updateSequence(client_id: any, flow_id: any, sequence_id: any, data: any) {
    return this.http.put(`${this.apiURL}/api/flows/${flow_id}/sequences/${sequence_id}?client_id=${client_id}`, data);
  }

  public deleteSequence(client_id: any, flow_id: any, sequence_id: any) {
    return this.http.delete(`${this.apiURL}/api/flows/${flow_id}/sequences/${sequence_id}?client_id=${client_id}`);
  }

  public appendSequences(client_id: any, flow_id: any, data: any) {
    return this.http.put(`${this.apiURL}/api/flows/${flow_id}/sequences?client_id=${client_id}`, data);
  }

  public generateAiSequence(client_id: any, payload: any) {
    return this.http.post(`${this.apiURL}/api/flows/generate-sequence?client_id=${client_id}`, payload);
  }

  public attachFlowToCustomer(client_id: any, flow_id: any, payload: any) {
    return this.http.post(`${this.apiURL}/api/flows/${flow_id}/attach?client_id=${client_id}`, payload);
  }

  // Nurturing Flow Audience & Assignment APIs
  public getCustomersWithFilters(clientId: any, options: {
    lead_status?: string;
    lead_stage?: string;
    exclude_lead_stages?: string;
    lead_manager_filter?: string;
    min_lead_score?: number | null;
    min_score?: number | null;
    budget_lt?: number | null;
    budget_gt?: number | null;
    budget_eq?: number | null;
    requirement_key?: string;
    requirement_operator?: string;
    requirement_value?: string;
    product_ids?: string | number[];
    flow_id?: number | null;
    campaign_id?: string | number;
    tags?: string | string[];
    from_date?: string;
    to_date?: string;
    filter_by_created_date?: boolean;
    search?: string;
    search_scope?: 'customer' | 'chat' | 'all' | string;
    chat_keyword?: string;
    include_inactive?: boolean;
    show_all?: boolean;
    include_chat_context?: boolean;
    include_nurture_flows?: boolean;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }): Observable<any> {
    let params = new HttpParams().set('client_id', clientId.toString());
    if (options.search) params = params.set('search', options.search);
    if (options.search_scope) params = params.set('search_scope', options.search_scope);
    if (options.chat_keyword) params = params.set('chat_keyword', options.chat_keyword);
    if (options.lead_status) params = params.set('lead_status', options.lead_status);
    if (options.lead_stage) params = params.set('lead_stage', options.lead_stage);
    if (options.exclude_lead_stages) params = params.set('exclude_lead_stages', options.exclude_lead_stages);
    if (options.lead_manager_filter) params = params.set('lead_manager_filter', options.lead_manager_filter);
    
    const minScore = options.min_lead_score !== undefined && options.min_lead_score !== null 
      ? options.min_lead_score 
      : options.min_score;
    if (minScore !== undefined && minScore !== null) {
      params = params.set('min_lead_score', minScore.toString());
    }
    if (options.budget_lt !== undefined && options.budget_lt !== null) {
      params = params.set('budget_lt', options.budget_lt.toString());
    }
    if (options.budget_gt !== undefined && options.budget_gt !== null) {
      params = params.set('budget_gt', options.budget_gt.toString());
    }
    if (options.budget_eq !== undefined && options.budget_eq !== null) {
      params = params.set('budget_eq', options.budget_eq.toString());
    }
    if (options.requirement_key) {
      params = params.set('requirement_key', options.requirement_key);
    }
    if (options.requirement_operator) {
      params = params.set('requirement_operator', options.requirement_operator);
    }
    if (options.requirement_value) {
      params = params.set('requirement_value', options.requirement_value);
    }
    if (options.campaign_id !== undefined && options.campaign_id !== null) {
      params = params.set('campaign_id', options.campaign_id.toString());
    }
    if (options.tags) {
      const tagStr = Array.isArray(options.tags) ? options.tags.join(',') : options.tags.toString();
      if (tagStr) params = params.set('tags', tagStr);
    }
    if (options.from_date) params = params.set('from_date', options.from_date);
    if (options.to_date) params = params.set('to_date', options.to_date);
    if (options.filter_by_created_date) params = params.set('filter_by_created_date', 'true');
    if (options.include_chat_context !== undefined && options.include_chat_context !== null) {
      params = params.set('include_chat_context', options.include_chat_context ? 'true' : 'false');
    }
    if (options.include_nurture_flows !== undefined && options.include_nurture_flows !== null) {
      params = params.set('include_nurture_flows', options.include_nurture_flows ? 'true' : 'false');
    }
    if (options.include_inactive) {
      params = params.set('include_inactive', 'true');
    }
    if (options.show_all) {
      params = params.set('show_all', 'true');
    }
    if (options.product_ids) {
      const prodStr = Array.isArray(options.product_ids) ? options.product_ids.join(',') : options.product_ids.toString();
      if (prodStr) params = params.set('product_ids', prodStr);
    }
    if (options.flow_id !== undefined && options.flow_id !== null) {
      params = params.set('flow_id', options.flow_id.toString());
    }
    if (options.limit !== undefined && options.limit !== null) {
      params = params.set('limit', options.limit.toString());
    }
    if (options.offset !== undefined && options.offset !== null) {
      params = params.set('offset', options.offset.toString());
    }
    if (options.sort_by) params = params.set('sort_by', options.sort_by);
    if (options.sort_order) params = params.set('sort_order', options.sort_order);

    return this.http.get<any>(`${this.apiURL}/customer/get_customers_by_client_id`, { params });
  }

   public getCustomersWithFiltersC(clientId: any, options: {
    lead_status?: string;
    lead_stage?: string;
    exclude_lead_stages?: string;
    lead_manager_filter?: string;
    min_lead_score?: number | null;
    budget_lt?: number | null;
    budget_gt?: number | null;
    budget_eq?: number | null;
    requirement_key?: string;
    requirement_operator?: string;
    requirement_value?: string;
    contact_id?: number | null;
    group_id?: number | null;
    product_ids?: string | number[];
    flow_id?: number | null;
    campaign_id?: string | number;
    tags?: string | string[];
    from_date?: string;
    to_date?: string;
    filter_by_created_date?: boolean;
    search?: string;
    search_scope?: 'customer' | 'chat' | 'all' | string;
    chat_keyword?: string;
    include_inactive?: boolean;
    show_all?: boolean;
    include_chat_context?: boolean;
    include_nurture_flows?: boolean;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }): Observable<any> {
    let params = new HttpParams().set('client_id', clientId.toString());
    if (options.search) params = params.set('search', options.search);
    if (options.search_scope) params = params.set('search_scope', options.search_scope);
    if (options.chat_keyword) params = params.set('chat_keyword', options.chat_keyword);
    if (options.lead_status) params = params.set('lead_status', options.lead_status);
    if (options.lead_stage) params = params.set('lead_stage', options.lead_stage);
    if (options.exclude_lead_stages) params = params.set('exclude_lead_stages', options.exclude_lead_stages);
    if (options.lead_manager_filter) params = params.set('lead_manager_filter', options.lead_manager_filter);
    if (options.min_lead_score !== undefined && options.min_lead_score !== null) {
      params = params.set('min_lead_score', options.min_lead_score.toString());
    }
    if (options.budget_lt !== undefined && options.budget_lt !== null) {
      params = params.set('budget_lt', options.budget_lt.toString());
    }
    if (options.budget_gt !== undefined && options.budget_gt !== null) {
      params = params.set('budget_gt', options.budget_gt.toString());
    }
    if (options.budget_eq !== undefined && options.budget_eq !== null) {
      params = params.set('budget_eq', options.budget_eq.toString());
    }
    if (options.requirement_key) params = params.set('requirement_key', options.requirement_key);
    if (options.requirement_operator) params = params.set('requirement_operator', options.requirement_operator);
    if (options.requirement_value) params = params.set('requirement_value', options.requirement_value);
    if (options.contact_id !== undefined && options.contact_id !== null) {
      params = params.set('contact_id', options.contact_id.toString());
    }
    if (options.group_id !== undefined && options.group_id !== null) {
      params = params.set('group_id', options.group_id.toString());
    }
    if (options.flow_id !== undefined && options.flow_id !== null) {
      params = params.set('flow_id', options.flow_id.toString());
    }
    if (options.campaign_id !== undefined && options.campaign_id !== null) {
      params = params.set('campaign_id', options.campaign_id.toString());
    }
    if (options.tags) {
      const tagStr = Array.isArray(options.tags) ? options.tags.join(',') : options.tags.toString();
      if (tagStr) params = params.set('tags', tagStr);
    }
    if (options.from_date) params = params.set('from_date', options.from_date);
    if (options.to_date) params = params.set('to_date', options.to_date);
    if (options.filter_by_created_date) params = params.set('filter_by_created_date', 'true');
    if (options.include_inactive) params = params.set('include_inactive', 'true');
    if (options.show_all) params = params.set('show_all', 'true');
    if (options.include_chat_context !== undefined && options.include_chat_context !== null) {
      params = params.set('include_chat_context', options.include_chat_context ? 'true' : 'false');
    }
    if (options.include_nurture_flows) params = params.set('include_nurture_flows', 'true');
    if (options.product_ids) {
      const prodStr = Array.isArray(options.product_ids) ? options.product_ids.join(',') : options.product_ids.toString();
      if (prodStr) params = params.set('product_ids', prodStr);
    }
    if (options.limit !== undefined && options.limit !== null) {
      params = params.set('limit', options.limit.toString());
    }
    if (options.offset !== undefined && options.offset !== null) {
      params = params.set('offset', options.offset.toString());
    }
    if (options.sort_by) params = params.set('sort_by', options.sort_by);
    if (options.sort_order) params = params.set('sort_order', options.sort_order);

    return this.http.get<any>(`${this.apiURL}/customer/filter`, { params });
  }

  public filterCustomersUnified(clientId: any, payload: any): Observable<any> {
    const body = { ...payload, client_id: clientId };
    return this.http.post<any>(`${this.apiURL}/customer/filter`, body);
  }

  public advanceAudienceFilter(clientId: any, payload: any): Observable<any> {
    const body = { ...payload, client_id: clientId };
    return this.http.post<any>(`${this.apiURL}/customer/advance_audience_filter`, body);
  }

  public assignFlow(clientId: any, flowId: any, payload: {
    customer_ids?: any[];
    customer_uuids?: any[];
    group_ids?: any[];
  }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/flows/${flowId}/assign?client_id=${clientId}`, payload);
  }

  public assignBatchFlows(clientId: any, payload: {
    flow_ids: any[];
    customer_ids?: any[];
    customer_uuids?: any[];
    group_ids?: any[];
  }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/flows/assign-batch?client_id=${clientId}`, payload);
  }

  public getFlowCustomers(clientId: any, flowId: any, options?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Observable<any> {
    let params = new HttpParams().set('client_id', clientId.toString());
    if (options?.status) {
      params = params.set('status', options.status);
    }
    if (options?.limit !== undefined && options?.limit !== null) {
      params = params.set('limit', options.limit.toString());
    }
    if (options?.offset !== undefined && options?.offset !== null) {
      params = params.set('offset', options.offset.toString());
    }
    return this.http.get<any>(`${this.apiURL}/api/flows/${flowId}/customers`, { params });
  }

  public unassignFlow(clientId: any, flowId: any, payload: {
    customer_ids?: any[];
    group_ids?: any[];
  }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/flows/${flowId}/unassign?client_id=${clientId}`, payload);
  }

  public unassignCustomerFromFlow(clientId: any, flowId: any, customerId: any): Observable<any> {
    return this.http.delete<any>(`${this.apiURL}/api/flows/${flowId}/customers/${customerId}?client_id=${clientId}`);
  }

  public uploadRetargetFile(client_id: any, formData: FormData) {
    return this.http.post(`${this.apiURL}/api/upload`, formData);
  }

  public getRetargetBatchStatus(client_id: any, batch_id: any) {
    return this.http.get(`${this.apiURL}/api/batch/${batch_id}?client_id=${client_id}`);
  }

  public getOptOuts(client_id: any) {
    return this.http.get(`${this.apiURL}/api/optouts?client_id=${client_id}`);
  }

  public deleteOptOut(client_id: any, id: any) {
    return this.http.delete(`${this.apiURL}/api/optouts/${id}?client_id=${client_id}`);
  }

  ///////////////////////////////////// api calls
  public getProducts(client_id: any, limit?: number, offset?: number, search?: string) {
    let params = new HttpParams();
    if (limit !== undefined && limit !== null) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined && offset !== null) {
      params = params.set('offset', offset.toString());
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get(`${this.apiURL}/api/productsbyclientid/${client_id}`, { params });
  }

  public searchPublicCatalogs(clientUuid: string, query: string, page: number = 1, pageSize: number = 20) {
    let params = new HttpParams()
      .set('client_uuid', clientUuid)
      .set('query', query || '')
      .set('page', page.toString())
      .set('page_size', pageSize.toString());
    return this.http.get(`${this.apiURL}/api/public/catalogs/search`, { params });
  }

  public getClientNumberByUuid(clientUuid: string) {
    return this.http.get(`${this.apiURL}/api/clients/uuid/${clientUuid}/number`);
  }

  public addProducts(data: {}) {
    return this.http.post(`${this.apiURL}/api/products/`, data);
  }
  public addBulkProducts(data: {}) {
    return this.http.post(`${this.apiURL}/api/product/bulk_create_and_push`, data);
  }

  public productAdditionalDetails(data: {}) {
    return this.http.post(`${this.apiURL}/api/product/add_details`, data);
  }
  public saveParagraphToQdrant(payload: any) {
    return this.http.post(
      `${this.apiURL}/api/product/save_paragraph_to_qdrant`,
      payload
    );
  }

  public bulkCreateAndPushParagraph(payload: any[]) {
    return this.http.post(
      `${this.apiURL}/api/product/bulk_create_and_push_paragraph`,
      payload
    );
  }

  public getProductById(id: any) {
    return this.http.get(`${this.apiURL}/api/products/uuid/${id}`);
  }

  public updateProductById(id: any, data: {}) {
    return this.http.put(`${this.apiURL}/api/products/uuid/${id}`, data);
  }
  public deleteProductById(id: any) {
    return this.http.delete(`${this.apiURL}/api/products/${id}`);
  }

  public createUser(data: {}) {
    return this.http.post(`${this.apiURL}/api/users/`, data);
  }

  public getUser() {
    return this.http.get(`${this.apiURL}/api/users/`);
  }
  public updateUser(user_id: any, data: {}) {
    return this.http.put(`${this.apiURL}/api/users/uuid/${user_id}`, data);
  }
  public deleteUser(user_id: any) {
    return this.http.delete(`${this.apiURL}/api/users/uuid/${user_id}`);
  }

  public activateUser(user_id: any) {
    return this.http.post(`${this.apiURL}/api/admin/users/${user_id}/activate`, { status: 'active' });
  }

  public createWAUser(data: {}) {
    return this.http.post(`${this.apiURL}/wa_middleware/create_user`, data);
  }

  public save_to_qdrant(data: {}) {
    return this.http.post(`${this.apiURL}/api/product/save_to_qdrant`, data);
  }

  public get_to_qdrant(data: {}, collectionName: string) {
    return this.http.post(`${this.apiURL}/api/qdrant/${collectionName}`, data);
  }

  update_to_qdrant(data: {}) {
    return this.http.post(`${this.apiURL}/api/product/edit_to_qdrant`, data);
  }
  edit_paragraph_to_qdrant(data: {}) {
    return this.http.post(`${this.apiURL}/api/product/edit_paragraph_to_qdrant`, data);
  }
  public CreateClient(data: {}) {
    return this.http.post(`${this.apiURL}/api/clients/`, data);
  }
  public getAllClient() {
    return this.http.get(`${this.apiURL}/api/clients`);
  }

  public getAllClientId(id: any) {
    return this.http.get(`${this.apiURL}/api/clients/uuid/${id}`);
  }
  public getAllClientByOrganization(organization_id: any) {
    return this.http.get(`${this.apiURL}/organizations/clients/by-organization/uuid/${organization_id}`);
  }

  public updateClientById(id: any, data: any) {
    return this.http.put(`${this.apiURL}/api/clients/uuid/${id}`, data);
  }

  public getPostSalesClient(organization_id: any) {
    return this.http.get(`${this.apiURL}/api/get-post-sales-client-by-organization-id/${organization_id}?agent_type=sales`);
  }


  public modelPreference(data: {}) {
    return this.http.post(`${this.apiURL}/api/model-preference`, data);
  }

  public getModelPreferenceById(id: any) {
    return this.http.get(`${this.apiURL}/api/model-preference/${id}`,);
  }

  public updateModelPreferenceById(client_id: any, data: {}) {
    return this.http.put(`${this.apiURL}/api/model-preference/${client_id}`, data);
  }

  public createInstance() {
    return this.http.post(`${this.apiURL}/wa_middleware/create_instance`, '');
  }

  public whatsappEmbeddedSignupSdkCallback(payload: any) {
    return this.http.post(`${this.apiURL}/api/embedded-signup/sdk-callback`, payload);
  }


  public getQrCode(id: {}) {
    return this.http.post(`${this.apiURL}/wa_middleware/get_qr_code`, id);
  }
  public getNewQrCode(id: {}) {
    return this.http.post(`${this.apiURL}/wa_middleware/get_qrcode_from_profile`, id);
  }

  public predefined_key() {
    return this.http.get(
      `${this.apiURL}/predefined_key/predefined_categoryall/`
    );
  }

  public getClientAndInstance(id: any) {
    return this.http.get(
      `${this.apiURL}/api/clients/${id}/instance_id`
    );
  }

  public checkLogin(id: any) {
    return this.http.post(
      `${this.apiURL}/wa_middleware/check_login?instance_id=${id}`, ''
    );
  }

  uploadImage(formData: FormData): Observable<any> {
    return this.http.post(`${this.apiURL}/file_upload/upload/1`, formData);
  }

  // resolver.service.ts
  public getChatSummary(id: any, params: any = {}) {
    // Convert params object to query string
    const queryString = new URLSearchParams();

    // Add all non-empty params to the query string
    for (const key in params) {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryString.append(key, params[key]);
      }
    }

    // return this.http.get(
    //   `${this.apiURL}/api/client/${id}/chat_summaries?${queryString.toString()}`
    // );
    return this.http.get(
      `${this.apiURL}/chat/client_chatsummary/grouped/${id}?${queryString.toString()}`
    );
    // return this.http.get(
    //   `${this.apiURL}/chat/client_chatsummary/grouped/${id}`
    // );
  }

  public getSummaryById(id: any) {
    return this.http.get(
      `${this.apiURL}/api/${id}`
    );
  }

  public updateSummaryById(id: any, data: any) {
    return this.http.put(
      `${this.apiURL}/api/${id}`,
      data
    );
  }

  public updateStage(session_id: any, data: {}) {
    return this.http.put(`${this.apiURL}/chat/update_session_stage/uuid/${session_id}`, data);
  }

  public moveSessionToNurturing(session_id: any, data: { flow_id?: number | null } = {}) {
    return this.http.post(`${this.apiURL}/chat/nurture/${session_id}`, data);
  }

  public moveSessionToNurturingByUuid(uuid: any, data: { flow_id?: number | null } = {}) {
    return this.http.post(`${this.apiURL}/chat/nurture/uuid/${uuid}`, data);
  }

  public updateStageAll(client_id: any, data: {}) {
    return this.http.put(`${this.apiURL}/chat/client/update_stage_list/${client_id}`, data);
  }


  public addCampaigns(data: any) {
    return this.http.post(
      `${this.apiURL}/campaigns/`, data);
  }
  public getCampaignsByClientId(client_id: any) {
    return this.http.get(`${this.apiURL}/campaigns/meta_structure/${client_id}`);
  }
  public getCampaignById(campaign_id: any) {
    return this.http.get(`${this.apiURL}/campaigns/${campaign_id}`);
  }
  public updateCampaignsById(campaign_id: any, data: any) {
    return this.http.put(`${this.apiURL}/campaigns/${campaign_id}`, data);
  }

  public getALLCustomer(client_id: any, limit?: number, offset?: number, search?: string) {
    let params = new HttpParams();
    if (limit !== undefined && limit !== null) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined && offset !== null) {
      params = params.set('offset', offset.toString());
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get(`${this.apiURL}/customer/get_customers_by_client_id`, { params });
  }
  public postTeamResponse(session_id: any, data: any) {
    return this.http.post(
      `${this.apiURL}/chat/add_team_response/uuid/${session_id}?team_response=${data}`, '');
  }

  public postExcludeBulk(data: any) {
    return this.http.post(
      `${this.apiURL}/exclude/excludes/bulk`, data);
  }
  public postExclude(data: any) {
    return this.http.post(
      `${this.apiURL}/exclude/excludes`, data);
  }
  public getExclude(client_id: any, search?: string, limit?: number, offset?: number) {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (limit !== undefined && limit !== null) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined && offset !== null) {
      params = params.set('offset', offset.toString());
    }
    return this.http.get(
      `${this.apiURL}/exclude/excludes/client/${client_id}`, { params });
  }

  public updateExcludeById(exclude_id: any, data: any) {
    return this.http.put(
      `${this.apiURL}/exclude/excludes/${exclude_id}`, data);
  }
  public updateExcludeByDelete(exclude_id: any) {
    return this.http.delete(
      `${this.apiURL}/exclude/excludes/${exclude_id}`);
  }

  public postIncludeBulk(data: any) {
    return this.http.post(
      `${this.apiURL}/api/v1/allowed-contacts/bulk`, data);
  }
  public postInclude(data: any) {
    return this.http.post(
      `${this.apiURL}/api/v1/allowed-contacts/`, data);
  }
  public getInclude(client_id: any, search?: string, limit?: number, offset?: number) {
    let params = new HttpParams();
    params = params.set('client_id', client_id.toString());
    if (search) {
      params = params.set('search', search);
    }
    if (limit !== undefined && limit !== null) {
      params = params.set('limit', limit.toString());
    }
    if (offset !== undefined && offset !== null) {
      params = params.set('offset', offset.toString());
    }
    return this.http.get(
      `${this.apiURL}/api/v1/allowed-contacts/`, { params });
  }
  public updateIncludeById(include_id: any, data: any) {
    return this.http.put(
      `${this.apiURL}/api/v1/allowed-contacts/${include_id}`, data);
  }
  public updateIncludeByDelete(include_id: any) {
    return this.http.delete(
      `${this.apiURL}/api/v1/allowed-contacts/${include_id}`);
  }

  public updateProductStatus(product_id: any, data: {}) {
    return this.http.put(`${this.apiURL}/api/product/${product_id}/status`, data);
  }

  getClientDashboard(params: { client_id: any, from_date?: string, to_date?: string }) {
    let httpParams = new HttpParams().set('client_id', params.client_id);

    if (params.from_date) {
      httpParams = httpParams.set('from_date', params.from_date);
    }

    if (params.to_date) {
      httpParams = httpParams.set('to_date', params.to_date);
    }

    return this.http.get(`${this.apiURL}/api/dashboard`, { params: httpParams });
  }

  // public webhookUrlApi(Instance_id:any,token:any) {
  //   return this.http.post(`${this.apiURL}/wa_middleware/set_webhook?webhook_url=${this.apiURL}/api/webhook&enable=true&instance_id=${Instance_id}&access_token=${token}`,'' );
  // }

  public webhookUrlApi(data: any) {
    data.webhook_url = `${this.apiURL}/api/v2/webhook/whatsappweb`;
    return this.http.post(`${this.apiURL}/wa_middleware/set_webhook`, data);
  }

  public getUserById(user_id: any) {
    return this.http.get(`${this.apiURL}/api/users/uuid/${user_id}`);
  }
  public bookCalendar(data: any) {
    return this.http.post(`${this.apiURL}/calendar/book`, data);
  }
  public deleteBookCalendar(calendar_id: any) {
    return this.http.delete(`${this.apiURL}/calendar/calendar/delete_booking/${calendar_id}`);
  }
  public changeCalendarPrice(data: any) {
    return this.http.post(`${this.apiURL}/calendar/`, data);
  }
  public getCalendarId(product_id: any, start_date: any, end_date: any) {
    return this.http.get(`${this.apiURL}/calendar/product/${product_id}/range?start_date=${start_date}&end_date=${end_date}`);
  }

  public createCustomer(payload: any) {
    return this.http.post(`${this.apiURL}/customer/create_customer`, payload);
  }
  public getCustomerById(customer_id: any) {
    return this.http.post(`${this.apiURL}/customer/trusted/decrypt_customer_phone/uuid/${customer_id}`, '');
  }
  public updateCustomer(customer_id: any, payload: any) {
    return this.http.put(`${this.apiURL}/customer/uuid/${customer_id}`, payload);
  }
  public CreateAndUpdateCustomer(client_id: any, payload: any) {
    return this.http.post(`${this.apiURL}/customer/bulk_upsert_customers?client_id=${client_id}`, payload);
  }

  public createConversion(payload: any) {
    return this.http.post(`${this.apiURL}/api/conversions/`, payload);
  }

  public getConversions(params: any = {}) {
    let httpParams = new HttpParams();
    for (const key in params) {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        httpParams = httpParams.set(key, params[key].toString());
      }
    }
    return this.http.get(`${this.apiURL}/api/conversions/`, { params: httpParams });
  }

  public getConversionById(conversion_id: any) {
    return this.http.get(`${this.apiURL}/api/conversions/${conversion_id}`);
  }

  public updateConversion(conversion_id: any, payload: any) {
    return this.http.put(`${this.apiURL}/api/conversions/${conversion_id}`, payload);
  }

  public deleteConversion(conversion_id: any) {
    return this.http.delete(`${this.apiURL}/api/conversions/${conversion_id}`);
  }


  public updateAutoFollowUp(payload: any) {
    return this.http.post(`${this.apiURL}/chat/update-followup-status/`, payload);
  }
  public updateAutoResponse(payload: any) {
    return this.http.post(`${this.apiURL}/chat/update-auto-response/`, payload);
  }

  public addNewLeads(data: any) {
    return this.http.post(`${this.apiURL}/chat/create-session-with-team-response/`, data);
  }

  public bulkUploadLeads(data: any) {
    return this.http.post(`${this.apiURL}/chat/sessions/bulk-create/`, data);
  }

  public discardSession(sessionId: any) {
    return this.http.post(`${this.apiURL}/chat/discard/${sessionId}`, {});
  }

  public discardSessionByUuid(uuid: any) {
    return this.http.post(`${this.apiURL}/chat/discard/uuid/${uuid}`, {});
  }

  public sendMessage(data: any) {
    return this.http.post(`${this.apiURL}/wa_middleware/send_message`, data);
  }

  public triggerFollowup(data: any, session_id: any) {
    return this.http.post(`${this.apiURL}/chat/trigger_followup/uuid/${session_id}?message=${data}`, '');
  }

  public getScheduledFollowups(params: any = {}) {
    return this.http.get(`${this.apiURL}/api/followups/scheduled`, {
      params,
      observe: 'response'
    });
  }

  public scheduleManualFollowup(sessionId: number | string, data: { followup_message: string, time_offset_minutes?: number, scheduled_at?: string }) {
    return this.http.post(`${this.apiURL}/api/followup/${sessionId}/schedule`, data);
  }

  public cancelFollowup(sessionId: number | string) {
    return this.http.post(`${this.apiURL}/api/followup/${sessionId}/cancel`, {});
  }

  public updateSession_data(data: any, session_id: any) {
    return this.http.put(`${this.apiURL}/chat/session/uuid/${session_id}`, data);
  }

  public getSummaryWithLeadStatus(data: any) {
    return this.http.post(`${this.apiURL}/chat/leads_stage_filter`, data);
  }

  public exportOpportunities(payload: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/reports/leads/export`, payload, {
      responseType: 'blob'
    });
  }

  public getSummaryWithValue(data: any, client_id: any) {
    return this.http.post(`${this.apiURL}/chat/chat_summaries/filter?client_id=${client_id}`, data);
  }
  public generateToken(product_id: any) {
    return this.http.post(`${this.apiURL}/utils/calendar/generate-token?product_id=${product_id}`, '');
  }
  public deleteQdrant(data: any) {
    return this.http.post(`${this.apiURL}/qdrant/delete_point/`, data);
  }
  public getManagerByUser(data: any) {
    return this.http.post(`${this.apiURL}/api/users/manager-hierarchy`, data);
  }
  public createManager(data: any) {
    return this.http.post(`${this.apiURL}/api/users/manager`, data);
  }
  public updateManager(user_id: any, data: any) {
    return this.http.put(`${this.apiURL}/api/users/uuid/${user_id}`, data);
  }
  public deleteManager(user_id: any) {
    return this.http.delete(`${this.apiURL}/api/users/uuid/${user_id}`);
  }
  public createStaff(data: any) {
    return this.http.post(`${this.apiURL}/api/users_staff/`, data);
  }

  // CRM Chatter & Notifications API Integration
  public getNotifications(params: any = true) {
    let httpParams: any = {};
    if (typeof params === 'boolean') {
      httpParams = { bundle_reminders: String(params) };
    } else if (typeof params === 'object' && params !== null) {
      httpParams = { ...params };
      if (httpParams.bundle_reminders === undefined) {
        httpParams.bundle_reminders = 'true';
      }
    }
    return this.http.get(`${this.apiURL}/api/notifications`, {
      params: httpParams
    });
  }

  public getClientWiseNotifications(params: any = true) {
    let httpParams: any = {};
    if (typeof params === 'boolean') {
      httpParams = { bundle_reminders: String(params) };
    } else if (typeof params === 'object' && params !== null) {
      httpParams = { ...params };
      if (httpParams.bundle_reminders === undefined) {
        httpParams.bundle_reminders = 'true';
      }
    }
    return this.http.get(`${this.apiURL}/api/notifications/client-wise`, {
      params: httpParams
    });
  }

  public readNotification(notificationId: any) {
    return this.http.put(`${this.apiURL}/api/notifications/${notificationId}/read`, {});
  }

  public getChatterActivities(sessionId: any, filters?: { search?: string; status?: string; contact_id?: number; group_id?: number }) {
    let params: any = {};
    if (filters) {
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.contact_id !== undefined && filters.contact_id !== null) params.contact_id = filters.contact_id;
      if (filters.group_id !== undefined && filters.group_id !== null) params.group_id = filters.group_id;
    }
    return this.http.get(`${this.apiURL}/api/sessions/${sessionId}/chatter/activities`, { params });
  }

  public getClientChatterActivities(clientId: any, filters?: { search?: string; status?: string; contact_id?: number; group_id?: number; skip?: number; limit?: number }) {
    let params: any = {};
    if (filters) {
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.contact_id !== undefined && filters.contact_id !== null) params.contact_id = filters.contact_id;
      if (filters.group_id !== undefined && filters.group_id !== null) params.group_id = filters.group_id;
      if (filters.skip !== undefined && filters.skip !== null) params.skip = filters.skip;
      if (filters.limit !== undefined && filters.limit !== null) params.limit = filters.limit;
    }
    return this.http.get(`${this.apiURL}/api/clients/${clientId}/chatter/activities`, { params });
  }

  public createChatterActivity(sessionId: any, data: any) {
    return this.http.post(`${this.apiURL}/api/sessions/${sessionId}/chatter/activities`, data);
  }
  public updateChatterActivity(sessionId: any, activityId: any, data: any) {
    return this.http.put(`${this.apiURL}/api/sessions/${sessionId}/chatter/activities/${activityId}`, data);
  }
  public completeChatterActivity(sessionId: any, activityId: any) {
    return this.http.post(`${this.apiURL}/api/sessions/${sessionId}/chatter/activities/${activityId}/done`, {});
  }
  public deleteChatterActivity(sessionId: any, activityId: any) {
    return this.http.delete(`${this.apiURL}/api/sessions/${sessionId}/chatter/activities/${activityId}`);
  }

  public getChatterLogs(sessionId: any, filtersOrSearch?: string | { search?: string; contact_id?: number; group_id?: number }) {
    let params: any = {};
    if (typeof filtersOrSearch === 'string') {
      if (filtersOrSearch) params.search = filtersOrSearch;
    } else if (filtersOrSearch && typeof filtersOrSearch === 'object') {
      if (filtersOrSearch.search) params.search = filtersOrSearch.search;
      if (filtersOrSearch.contact_id !== undefined && filtersOrSearch.contact_id !== null) params.contact_id = filtersOrSearch.contact_id;
      if (filtersOrSearch.group_id !== undefined && filtersOrSearch.group_id !== null) params.group_id = filtersOrSearch.group_id;
    }
    return this.http.get(`${this.apiURL}/api/sessions/${sessionId}/chatter/logs`, { params });
  }

  public getClientChatterLogs(clientId: any, filters?: { search?: string; contact_id?: number; group_id?: number; skip?: number; limit?: number }) {
    let params: any = {};
    if (filters) {
      if (filters.search) params.search = filters.search;
      if (filters.contact_id !== undefined && filters.contact_id !== null) params.contact_id = filters.contact_id;
      if (filters.group_id !== undefined && filters.group_id !== null) params.group_id = filters.group_id;
      if (filters.skip !== undefined && filters.skip !== null) params.skip = filters.skip;
      if (filters.limit !== undefined && filters.limit !== null) params.limit = filters.limit;
    }
    return this.http.get(`${this.apiURL}/api/clients/${clientId}/chatter/logs`, { params });
  }

  public postChatterLog(sessionId: any, data: any) {
    return this.http.post(`${this.apiURL}/api/sessions/${sessionId}/chatter/logs`, data);
  }

  public getVisibleSessions(params?: { contact_id?: number; group_id?: number; search?: string }) {
    return this.http.get(`${this.apiURL}/visible-sessions`, { params });
  }

  public getMySessions(params?: { contact_id?: number; group_id?: number; search?: string }) {
    return this.http.get(`${this.apiURL}/my-sessions`, { params });
  }

  public filterChatSummariesSearch(payload: { client_id: number; contact_id?: number; group_id?: number; lead_stage?: string; page?: number; page_size?: number; [key: string]: any }) {
    return this.http.post(`${this.apiURL}/chat/client_chatsummary/filter_search`, payload);
  }

  public getPublicChatSummary(id: any, params: any = {}) {
    const queryString = new URLSearchParams();
    for (const key in params) {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryString.append(key, params[key]);
      }
    }
    const query = queryString.toString();
    return this.http.get(`${this.apiURL}/chat/client_chatsummary/grouped/public/${id}${query ? '?' + query : ''}`);
  }

  public uploadChatterAttachment(sessionId: any, formData: FormData) {
    return this.http.post(`${this.apiURL}/api/sessions/${sessionId}/chatter/attachments`, formData);
  }

  public resetInstance(instance_id: any) {
    return this.http.post(`${this.apiURL}/wa_middleware/reset_instance?instance_id=${instance_id}`, '');
  }
  public rebootInstance(instance_id: any) {
    return this.http.post(`${this.apiURL}/wa_middleware/reboot_instance?instance_id=${instance_id}`, '');
  }
  public updateAgentStatus(client_id: any, data: any) {
    return this.http.patch(`${this.apiURL}/api/clients/uuid/${client_id}/status`, data);
  }

  public updateAgentAiResponseStatus(client_id: any, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/api/clients/uuid/${client_id}/ai_response`, data);
  }

  public leadAssignUser(client_id: any, data: any) {
    return this.http.put(`${this.apiURL}/api/clients/uuid/${client_id}/status`, data);
  }

  public reconnectInstance(instance_id: any) {
    return this.http.post(`${this.apiURL}/wa_middleware/reconnect_instance?instance_id=${instance_id}`, '');
  }

  //// Organizations api
  public createOrganizations(data: any) {
    return this.http.post(`${this.apiURL}/organizations/`, data);
  }
  public getOrganizations() {
    return this.http.get(`${this.apiURL}/organizations/`);
  }
  public getOrganizationById(organizationId: any) {
    return this.http.get(`${this.apiURL}/organizations/uuid/${organizationId}`);
  }
  public getOrganizationAccountAlerts(organizationId: any) {
    return this.http.get(`${this.apiURL}/organizations/${organizationId}/account-alerts`);
  }
  public getOrganizationBalance(organizationId: any) {
    return this.http.get(`${this.apiURL}/organizations/${organizationId}/balance`);
  }
  public getOrganizationBalanceByUuid(organizationUuid: any) {
    return this.http.get(`${this.apiURL}/organizations/uuid/${organizationUuid}/balance`);
  }
  public getOrganizationInvoices(organizationId: any) {
    return this.http.get(`${this.apiURL}/organizations/${organizationId}/invoices`);
  }
  public getOrganizationPayments(organizationId: any) {
    return this.http.get(`${this.apiURL}/organizations/${organizationId}/payments`);
  }
  public getOrganizationSubscriptions(organizationId: any) {
    return this.http.get(`${this.apiURL}/organizations/${organizationId}/subscriptions`);
  }
  public getBusinessProfile() {
    return this.http.get(`${this.apiURL}/api/embedded-signup/business-profile`);
  }
  public updateOrganizationById(organizationId: any, data: any) {
    return this.http.put(`${this.apiURL}/organizations/uuid/${organizationId}`, data);
  }
  public deleteOrganizations(organizationId: any) {
    return this.http.delete(`${this.apiURL}/organizations/uuid/${organizationId}`);
  }

  /// Coupons api
  public createCoupons(data: any) {
    return this.http.post(`${this.apiURL}/coupons`, data);
  }
  public getCoupons() {
    return this.http.get(`${this.apiURL}/coupons`);
  }
  public updateCoupons(coupon_id: any, data: any) {
    return this.http.put(`${this.apiURL}/coupons/${coupon_id}`, data);
  }
  public deleteCoupons(coupon_id: any) {
    return this.http.delete(`${this.apiURL}/coupons/${coupon_id}`);
  }

  /// tax rate api
  public createTax_rate(data: any) {
    return this.http.post(`${this.apiURL}/tax-rates`, data);
  }
  public getTax_rate() {
    return this.http.get(`${this.apiURL}/tax-rates`);
  }
  public updateTax_rate(tax_rate_id: any, data: any) {
    return this.http.put(`${this.apiURL}/tax-rates/${tax_rate_id}`, data);
  }
  public deleteTax_rate(tax_rate_id: any) {
    return this.http.delete(`${this.apiURL}/tax-rates/${tax_rate_id}`);
  }

  /// Subscriptions api
  public createSubscriptions(data: any) {
    return this.http.post(`${this.apiURL}/subscriptions`, data);
  }
  public getSubscriptions() {
    return this.http.get(`${this.apiURL}/subscriptions`);
  }
  public getSubscriptionsByOrganizationsId(organization_id: any) {
    return this.http.get(`${this.apiURL}/subscriptions/organization/${organization_id}`);
  }
  public updateSubscriptions(subscription_id: any, data: any) {
    return this.http.put(`${this.apiURL}/subscriptions/${subscription_id}`, data);
  }
  public upgradeSubscription(subscription_id: any, data: { new_plan_id: number, idempotency_key: string }) {
    return this.http.post(`${this.apiURL}/subscriptions/${subscription_id}/upgrade`, data);
  }
  public deleteSubscriptions(subscription_id: any) {
    return this.http.delete(`${this.apiURL}/subscriptions/${subscription_id}`);
  }

  /// plans api
  public createPlans(data: any) {
    return this.http.post(`${this.apiURL}/plans`, data);
  }
  public getPlans(category?: string) {
    let url = `${this.apiURL}/plans/`;
    if (category) {
      url += `?category=${encodeURIComponent(category)}`;
    }
    return this.http.get(url);
  }
  public getPlansById(plan_id: any) {
    return this.http.get(`${this.apiURL}/plans/${plan_id}`);
  }
  public updatePlans(plan_id: any, data: any) {
    return this.http.put(`${this.apiURL}/plans/${plan_id}`, data);
  }
  public deletePlans(plan_id: any) {
    return this.http.delete(`${this.apiURL}/plans/${plan_id}`);
  }

  public assignOpportunityOther(data: any) {
    return this.http.post(`${this.apiURL}/chat/lead_transfer_to_other_client?session_id=${data.session_id}&new_client_id=${data.client_id}`, '');
  }

  public assignOpportunityCurrent(data: any) {
    return this.http.put(`${this.apiURL}/api/assign-lead-manually`, data);
  }
  public sessionsSummaryToSend(data: any) {
    return this.http.post(`${this.apiURL}/chat/sessions_summary_to_send/`, data);
  }

  // Create a new project

  createProject(projectData: any): Observable<any> {
    return this.http.post(`${this.apiURL}/projects`, projectData);
  }

  // Update an existing project
  updateProject(projectId: number, projectData: any): Observable<any> {
    return this.http.put(`${this.apiURL}/projects/${projectId}`, projectData);
  }

  // Get project by ID
  getProject(projectId: number): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/${projectId}`);
  }
  getProjectByOrganization(organization_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/projects/by_organization/${organization_id}`);
  }
  // Get all projects
  getProjects(): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/`);
  }

  // Delete a project
  deleteProject(projectId: number): Observable<any> {
    return this.http.delete(`${this.apiURL}/projects/${projectId}`);
  }

  // Create a new milestones
  createMilestone(milestonesdata: any, id: any): Observable<any> {
    return this.http.post(`${this.apiURL}/projects/${id}/milestones/bulk`, milestonesdata);
  }
  getMilestonesByProjectId(project_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/${project_id}/milestones`);
  }
  updateMilestones(milestone_data: any, milestone_id: any): Observable<any> {
    return this.http.put(`${this.apiURL}/projects/milestones/${milestone_id}`, milestone_data);
  }
  getMilestone(milestone_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/milestones/${milestone_id}`);
  }
  deleteMilestone(milestone_id: any): Observable<any> {
    return this.http.delete(`${this.apiURL}/projects/milestones/${milestone_id}`);
  }

  // Tasks & Milestones API (Order Management) as per integration guide
  getTasks(params?: { tenant_id?: string | number; assignee_phone?: string; status?: string; task_type?: string }): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.tenant_id !== undefined && params.tenant_id !== null) httpParams = httpParams.set('tenant_id', params.tenant_id.toString());
      if (params.assignee_phone) httpParams = httpParams.set('assignee_phone', params.assignee_phone);
      if (params.status) httpParams = httpParams.set('status', params.status);
      if (params.task_type) httpParams = httpParams.set('task_type', params.task_type);
    }
    return this.http.get(`${this.apiURL}/api/v1/tasks`, { params: httpParams });
  }

  createTask(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/v1/tasks`, data);
  }

  updateTask(task_id: any, data: any): Observable<any> {
    return this.http.put(`${this.apiURL}/api/v1/tasks/${task_id}`, data);
  }

  updateMilestoneStatus(task_id: any, milestone_id: any, data: { status: string }): Observable<any> {
    return this.http.put(`${this.apiURL}/api/v1/tasks/${task_id}/milestones/${milestone_id}`, data);
  }

  // Create a new financePolicydata
  createfinancePolicy(financePolicydata: any, id: any): Observable<any> {
    return this.http.post(`${this.apiURL}/projects/${id}/finance-policy`, financePolicydata);
  }
  getFinancePolicyByProjectId(project_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/${project_id}/finance-policy`);
  }
  updateFinancePolicy(policy_id: any, financeDara: any): Observable<any> {
    return this.http.put(`${this.apiURL}/projects/finance-policy/${policy_id}`, financeDara);
  }
  deleteFinancePolicy(policy_id: any): Observable<any> {
    return this.http.delete(`${this.apiURL}/projects/finance-policy/${policy_id}`);
  }
  //    getFinancePolicyId(policy_id:any): Observable<any> {
  //   return this.http.get(`${this.apiURL}/projects/${policy_id}/finance-policy`);
  // }


  // Create a new delaywindow
  createDelay(delaydata: any, id: any): Observable<any> {
    return this.http.post(`${this.apiURL}/projects/${id}/delay-windows`, delaydata);
  }
  getDelaysByProjectId(project_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/${project_id}/delay-windows`);
  }
  getDelayWindowById(delay_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/projects/delay-windows/${delay_id}`);
  }
  updateDelayWindow(delay_id: any, delaydata: any): Observable<any> {
    return this.http.put(`${this.apiURL}/projects/delay-windows/${delay_id}`, delaydata);
  }

  ///create units
  // creatUnits(unitdata: any): Observable<any> {
  //   return this.http.post(`${this.apiURL}/units`, unitdata);
  // }
  creatUnits(unitdata: any): Observable<any> {
    return this.http.post(`${this.apiURL}/units/bulk_create`, unitdata);
  }
  getUnits(project_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/units/?project_id=${project_id}`);
  }
  getUnitById(unit_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/units/${unit_id}`);
  }
  updateUnit(unit_id: any, data: any): Observable<any> {
    return this.http.put(`${this.apiURL}/units/${unit_id}`, data);
  }
  deleteUnit(unit_id: any): Observable<any> {
    return this.http.delete(`${this.apiURL}/units/${unit_id}`);
  }

  /// create booking
  createBooking(bookingdata: any): Observable<any> {
    return this.http.post(`${this.apiURL}/bookings/`, bookingdata);
  }
  getBookingsByProjectId(project_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/bookings/?project_id=${project_id}`,);
  }
  getBookingById(booking_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/bookings/${booking_id}`,);
  }
  //  deleteBookingById(booking_id: any): Observable<any> {
  //   return this.http.delete(`${this.apiURL}/bookings/${booking_id}`);
  // }
  updateBooking(booking_id: any, data: any): Observable<any> {
    return this.http.put(`${this.apiURL}/bookings/${booking_id}`, data);
  }
  getBookingMilestonesById(booking_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/booking-milestones/booking/${booking_id}`,);
  }
  //   getBookingMilestonesByMilestoneId(milestone_id: any): Observable<any> {
  //   return this.http.get(`${this.apiURL}/booking-milestones/${milestone_id}`,);
  // }
  getBookingMilestonesByMilestoneId(milestone_id: any): Observable<any> {
    return this.http.post(`${this.apiURL}/bookings/customer-booking-milestones`, milestone_id);
  }
  updateBookingMilestones(booking_id: any, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/booking-milestones/booking/${booking_id}`, data);
  }
  /// create booking
  createBookingMilestones(createBookingMilestones: any): Observable<any> {
    return this.http.post(`${this.apiURL}/booking-milestones/bulk/`, createBookingMilestones);
  }

  /// create booking
  createTemplet(templetData: any): Observable<any> {
    return this.http.post(`${this.apiURL}/templates/`, templetData);
  }

  getTemplatesByOrganization(org_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/templates/org/${org_id}`);
  }
  getTemplatesByWithoutOrganization(): Observable<any> {
    return this.http.get(`${this.apiURL}/templates/no-organization`);
  }
  getTemplatesByTemplateId(template_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/templates/uuid/${template_id}`);
  }
  updateTemplate(templetData: any, template_id: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/templates/uuid/${template_id}`, templetData);
  }
  deleteTemplatesByTemplateId(template_id: any): Observable<any> {
    return this.http.delete(`${this.apiURL}/templates/uuid/${template_id}`);
  }


  createJourneyAction(journeyData: any): Observable<any> {
    return this.http.post(`${this.apiURL}/journey-actions/bulk`, journeyData);
  }
  getJourneyAction(project_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/journey-actions/project/${project_id}`);
  }

  updateJourneyAction(data: any) {
    return this.http.patch(`${this.apiURL}/journey-actions/${data.id}`, data);
  }
  deleteJourneyAction(actionId: number): Observable<any> {
    return this.http.delete(`${this.apiURL}/journey-actions/${actionId}`);
  }

  orgSmtp_sendEmail(orgSmtp_sendEmail: any): Observable<any> {
    return this.http.post(`${this.apiURL}/org-smtp/send-email`, orgSmtp_sendEmail);
  }
  org_smtp(org_smtp: any): Observable<any> {
    return this.http.post(`${this.apiURL}/org-smtp/`, org_smtp);
  }
  org_getsmtp(organization_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/org-smtp/organization/${organization_id}`);
  }
  updateOrgSmtp(id: number, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/org-smtp/${id}`, data);
  }

  whatsappSend_message(whatsappSend_message: any): Observable<any> {
    return this.http.post(`${this.apiURL}/wa_middleware/send_message`, whatsappSend_message);
  }
  timelineEventsBulk(timelineEventsBulk: any): Observable<any> {
    return this.http.post(`${this.apiURL}/timeline-events/bulk/`, timelineEventsBulk);
  }
  createBillAdditionalData(timelineEventsBulk: any): Observable<any> {
    return this.http.post(`${this.apiURL}/additional_data/additional_data/`, timelineEventsBulk);
  }
  createBroker(broker: any): Observable<any> {
    return this.http.post(`${this.apiURL}/brokers/`, broker);
  }

  updateBroker(broker_id: number, broker: any): Observable<any> {
    return this.http.put(`${this.apiURL}/brokers/${broker_id}`, broker);
  }

  getBroker(broker_id: number): Observable<any> {
    return this.http.get(`${this.apiURL}/brokers/${broker_id}`);
  }
  deleteBroker(broker_id: number): Observable<any> {
    return this.http.delete(`${this.apiURL}/brokers/${broker_id}`);
  }
  getAllBroker(organization_id: number): Observable<any> {
    return this.http.get(`${this.apiURL}/brokers/?organization_id=${organization_id}`);
  }
  // payment 

  createPayment(payment: any): Observable<any> {
    return this.http.post(`${this.apiURL}/payments/`, payment);
  }

  getPayment(): Observable<any> {
    return this.http.get(`${this.apiURL}/payments/`);
  }
  getPaymentById(payment_id: number) {
    return this.http.get(`${this.apiURL}/payments/${payment_id}/`);
  }
  updatePayment(payment_id: number, payment: any) {
    return this.http.put(`${this.apiURL}/payments/${payment_id}/`, payment);
  }
  ////document
  createDocument(document: any): Observable<any> {
    return this.http.post(`${this.apiURL}/documents/`, document);
  }
  updateDocument(doc_id: any, document: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/documents/${doc_id}`, document);
  }
  getDocuments(booking_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/documents/booking/${booking_id}`);
  }
  getDocumentsByDocumentId(doc_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/documents/${doc_id}`);
  }
  deleteDocumentsByDocumentId(doc_id: any): Observable<any> {
    return this.http.delete(`${this.apiURL}/documents/${doc_id}`);
  }

  createWaiver(document: any): Observable<any> {
    return this.http.post(`${this.apiURL}/waivers/`, document);
  }
  updateWaiver(waiver_id: any, document: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/waivers/${waiver_id}`, document);
  }
  getWaiverByBookingId(booking_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/waivers/booking/${booking_id}`);
  }
  loadFullBookingDetails(booking_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/bookings/${booking_id}/full-details`);
  }
  getWaiverByWaiverId(waiver_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/waivers/${waiver_id}`);
  }

  createMilestoneMedia(mediaData: any): Observable<any> {
    return this.http.post(`${this.apiURL}/milestone-media/`, mediaData);
  }

  // In your service
  getPricingPlans(): Observable<any> {
    return this.http.get(`${this.apiURL}/plans/`);
  }

  getPublicPricingPlans(): Observable<any> {
    return this.http.get(`${this.apiURL}/plans/public`);
  }

  selectSubscriptionPlan(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/subscriptions/select-plan`, data);
  }

  linkExistingSubscription(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/subscriptions/link-existing`, data);
  }

  startSubscription(subscriptionId: string | number, delayDays?: number): Observable<any> {
    let url = `${this.apiURL}/subscriptions/${subscriptionId}/start`;
    if (delayDays !== undefined && delayDays !== null) {
      url += `?delay_days=${delayDays}`;
    }
    return this.http.post(url, {});
  }

  requestAddon(subscription_id: string, data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/subscriptions/${subscription_id}/addons`, data);
  }

  manualTriggerSummary(data: { minutes: number }) {
    return this.http.post(`${this.apiURL}/manual_trigger_summary`, data);
  }

  instagramAuth(data: any) {
    return this.http.post(`${this.apiURL}/instagram/auth/start`, data);
  }
  instagramAutomations(data: any) {
    return this.http.post(`${this.apiURL}/instagram/automations`, data);
  }

  createAssignment(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/lead-assignment-rules/`, data);
  }
  getAssignment(): Observable<any> {
    return this.http.get(`${this.apiURL}/lead-assignment-rules/`);
  }
  updateAssignment(rule_id: any, data: any): Observable<any> {
    return this.http.put(`${this.apiURL}/lead-assignment-rules/${rule_id}`, data);
  }
  deleteAssignment(rule_id: any): Observable<any> {
    return this.http.delete(`${this.apiURL}/lead-assignment-rules/${rule_id}`);
  }

  /// connect instagram account
  getPostSync(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/instagram/posts/sync`, data);
  }
  getInstagramPost(
    account_id: any,
    start: any,
    limit: any,
    options: {
      autoSync?: boolean;
      forceSync?: boolean;
      syncNew?: boolean;
    } = {}
  ): Observable<any> {
    const { autoSync, forceSync, syncNew } = options;
    let params = new HttpParams().set('skip', start).set('limit', limit);

    if (autoSync === false) params = params.append('auto_sync', 'false');
    if (forceSync) params = params.append('force_sync', 'true');
    if (syncNew) params = params.append('sync_new', 'true');

    return this.http.get(
      `${this.apiURL}/instagram/posts/by-account/${account_id}`,
      { params }
    );
  }

  getInstagramPostById(post_id: any): Observable<any> {
    return this.http.get(`${this.apiURL}/instagram/posts/${post_id}`);
  }
  createInstagramAutomations(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/instagram/automations`, data);
  }

  updateInstagramAutomation(automationId: number, data: any): Observable<any> {
    return this.http.put(`${this.apiURL}/instagram/automations/${automationId}`, data);
  }
  verifyInstagramAccount(subscribe: any, VERIFY_TOKEN: any, CHALLENGE_TOKEN: any): Observable<any> {
    return this.http.post(`${this.apiURL}/instagram/webhook?hub.mode=${subscribe}&hub.verify_token=${VERIFY_TOKEN}&hub.challenge=${CHALLENGE_TOKEN}`, '');
  }
  getKnowledgeBase(
    clientId: number,
    collectionName: string,
    options?: {
      category?: string;
      sub_category?: string;
      limit?: number;
      offset?: number;
      search?: string;
    }
  ): Observable<any> {

    let params = new HttpParams().set('collection_name', collectionName);

    if (options?.category) {
      params = params.set('category', options.category);
    }

    if (options?.sub_category) {
      params = params.set('sub_category', options.sub_category);
    }

    if (options?.limit !== undefined && options?.limit !== null) {
      params = params.set('limit', options.limit.toString());
    }

    if (options?.offset !== undefined && options?.offset !== null) {
      params = params.set('offset', options.offset.toString());
    }

    if (options?.search) {
      params = params.set('search', options.search);
    }

    return this.http.get(
      `${this.apiURL}/api/knowledge-base/${clientId}`,
      { params }
    );
  }
  updateknowledgeBaseAndProducts(data: any): Observable<any> {
    return this.http.put(`${this.apiURL}/api/product/update_paragraph_in_qdrant`, data);
  }
  getInstagramAccountDetails(account_id: any): Observable<any> {
    // return this.http.get(`${this.apiURL}/instagram/accounts/${account_id}/automations`);
    return this.http.get(`${this.apiURL}/instagram/accounts/${account_id}/automations-with-steps`);
  }
  instagramDisconnect(account_id: any): Observable<any> {
    return this.http.delete(`${this.apiURL}/instagram/disconnect/${account_id}`);
  }
  purchaseCredits(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/purchase-credits`, data);
  }
  private dataSource = new BehaviorSubject<string | null>(null);

  // Expose as readonly observable
  public readonly currentData$ = this.dataSource.asObservable();

  // Method to update the data
  public updateData(data: string) {
    this.dataSource.next(data);
  }

  // WhatsApp API Integration Endpoints
  // public generateSocketToken(data: object) {
  //   return this.http.post(`${this.apiURL}/api/socketio/generate-token`, data);
  // }

  public embeddedSignupDisconnect() {
    return this.http.post(`${this.apiURL}/api/embedded-signup/disconnect`, '');
  }
  // public startEmbeddedSignup(client_id:any,token:any) {
  //   // const encodedUri = encodeURIComponent(redirect_uri);
  //   return this.http.get(`${this.apiURL}/api/embedded-signup/start?nx_client_id=${client_id}`,{headers:{'Authorization': `Bearer ${token}`}});
  // }

  // public checkSignupStatus(token:any) {
  //   return this.http.get(`${this.apiURL}/api/embedded-signup/status`,{headers:{'Authorization': `Bearer ${token}`}});
  // }

  // public exchangeToken(short_lived_token: string,token:any) {
  //   return this.http.post(`${this.apiURL}/api/embedded-signup/exchange-token`, { short_lived_token },{headers:{'Authorization': `Bearer ${token}`}});
  // }

  // public manualWhatsappSetup(data: { app_id: string, access_token: string, phone_number_id: string, waba_id: string },socket_token:any) {
  //   return this.http.post(`${this.apiURL}/api/whatsapp/manual-setup`, data,{headers:{'Authorization': `Bearer ${socket_token}`}});
  // }

  // public disconnectWhatsapp(waba_id: any) {
  //   return this.http.delete(`${this.apiURL}/api/whatsapp/disconnect/${waba_id}`);
  // }
  getPostById(post_id: number): Observable<any> {
    return this.http.get(`${this.apiURL}/instagram/posts/${post_id}`);
  }
  sendData(data: string): void {
    // Use setTimeout to ensure it runs in the next change detection cycle
    setTimeout(() => {
      this.dataSource.next(data);
    });
  }

  // Method to get current value
  getCurrentValue(): string | null {
    return this.dataSource.value;
  }

  // AI Prompt Management
  getAllPrompts(): Observable<any> {
    return this.http.get(`${this.apiURL}/api/additional-data/`);
  }

  getPromptByName(name: string): Observable<any> {
    return this.http.get(`${this.apiURL}/api/additional-data/byname/${name}`);
  }

  createPrompt(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/additional-data/`, data);
  }

  updatePrompt(id: number, data: any): Observable<any> {
    return this.http.put(`${this.apiURL}/api/additional-data/${id}`, data);
  }

  deletePrompt(id: number): Observable<any> {
    return this.http.delete(`${this.apiURL}/api/additional-data/${id}`);
  }

  validateKey(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/encryption/validate-key`, data);
  }

  getEncryptionConfig(): Observable<any> {
    return this.http.get(`${this.apiURL}/api/encryption/config`);
  }

  // Razorpay Integration Endpoints
  createRazorpaySubscription(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/razorpay/create-subscription/`, data);
  }

  createRazorpayOrder(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/razorpay/create-order/`, data);
  }

  verifyRazorpayPayment(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/razorpay/verify-payment/`, data);
  }

  startTrial(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/subscriptions/start-trial`, data);
  }

  activateTrial(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/subscriptions/activate-trial`, data);
  }

  reconnectAutopay(data?: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/subscriptions/reconnect-autopay`, data || {});
  }

  downgradeSubscription(subscriptionId: any, data: { new_plan_id: number }): Observable<any> {
    return this.http.post(`${this.apiURL}/api/subscriptions/${subscriptionId}/downgrade`, data);
  }

  verifyAutopayStatus(subscriptionId: any): Observable<any> {
    return this.http.post(`${this.apiURL}/api/subscriptions/verify-autopay/${subscriptionId}`, {});
  }

  getMySubscriptionStatus(): Observable<any> {
    return this.http.get(`${this.apiURL}/api/subscriptions/my-status`);
  }

  createeffortless(): Observable<any> {
    return this.http.post(`${this.apiURL}/api/whatsapp/effortless-setup`, '');
  }

  getConversionSettings(clientId: number): Observable<any> {
    return this.http.get<any>(
      `${this.apiURL}/api/client/conversion_settings/${clientId}`
    );
  }

  updateConversionSettings(clientId: number, settings: any): Observable<any> {
    return this.http.put<any>(
      `${this.apiURL}/chat/client/conversion_settings/${clientId}`,
      settings
    );
  }

  sendTestConversionEvent(sessionId: number, stageName: string, testEventCode?: string): Observable<any> {
    let params = new HttpParams().set('stage_name', stageName);
    if (testEventCode) {
      params = params.set('test_event_code', testEventCode);
    }
    return this.http.post<any>(
      `${this.apiURL}/api/chat/session/${sessionId}/test_conversion`,
      {},
      { params }
    );
  }

  sendCapiTestConversion(clientId: number, metaEvent: string, testEventCode?: string): Observable<any> {
    let params = new HttpParams().set('meta_event', metaEvent);
    if (testEventCode) {
      params = params.set('test_event_code', testEventCode);
    }
    return this.http.post<any>(
      `${this.apiURL}/api/clients/${clientId}/test_conversion`,
      {},
      { params }
    );
  }

  testAllCapiConversions(clientId: number, testEventCode: string): Observable<any> {
    let params = new HttpParams().set('test_event_code', testEventCode);
    return this.http.post<any>(
      `${this.apiURL}/chat/client/${clientId}/test_all_conversions`,
      {},
      { params }
    );
  }

  // --- Client Configuration & Settings APIs ---
  getClientConfig(clientId?: any): Observable<any> {
    let id = clientId;
    if (!id) {
      try {
        const client = JSON.parse(localStorage.getItem('client') || '{}');
        id = client?.id || client?.client_id;
      } catch (e) {}
    }
    return this.http.get<any>(`${this.apiURL}/api/clients/${id}/feature-config`);
  }

  updateClientConfig(arg1: any, arg2?: any): Observable<any> {
    let clientId: any;
    let payload: any;

    if ((typeof arg1 === 'number' || typeof arg1 === 'string') && arg2 !== undefined) {
      clientId = arg1;
      payload = arg2;
    } else {
      payload = arg1;
      clientId = arg2;
    }

    if (!clientId) {
      try {
        const client = JSON.parse(localStorage.getItem('client') || '{}');
        clientId = client?.id || client?.client_id;
      } catch (e) {}
    }

    return this.http.put<any>(`${this.apiURL}/api/clients/${clientId}/feature-config`, payload);
  }

  getKnowledgeSuggestions(status: string = 'PENDING', clientId?: number | string, skip: number = 0, limit: number = 50): Observable<any> {
    let params = new HttpParams()
      .set('status', status)
      .set('skip', skip.toString())
      .set('limit', limit.toString());
    if (clientId) {
      params = params.set('client_id', clientId.toString());
    }
    return this.http.get<any>(`${this.apiURL}/api/knowledge/ai_learning_suggestions`, { params });
  }

  approveKnowledgeSuggestion(suggestionId: number | string, editedKnowledge?: string): Observable<any> {
    const payload = editedKnowledge ? { edited_knowledge: editedKnowledge } : {};
    return this.http.post<any>(`${this.apiURL}/api/knowledge/ai_learning_suggestions/${suggestionId}/approve`, payload);
  }

  rejectKnowledgeSuggestion(suggestionId: number | string): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/knowledge/ai_learning_suggestions/${suggestionId}/reject`, {});
  }

  getActiveAiLearning(clientId?: number | string, limit?: number, offset?: string): Observable<any> {
    let params = new HttpParams();
    if (clientId) {
      params = params.set('client_id', clientId.toString());
    }
    if (limit !== undefined && limit !== null) {
      params = params.set('limit', limit.toString());
    }
    if (offset) {
      params = params.set('offset', offset);
    }
    return this.http.get<any>(`${this.apiURL}/api/knowledge/active_ai_learning`, { params });
  }

  checkKnowledgeExistence(payload: { client_id: number; manual_response: string; session_id?: number | string; recent_context?: any[] }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/knowledge/check-existence`, payload);
  }

  addManualKnowledge(payload: { client_id: number; category: string; question: string; answer: string }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/knowledge/add-manual`, payload);
  }

  generateOnboarding(payload: { client_id: number; source_data?: string; client_preferences?: string; history_limit?: number }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/knowledge/generate-onboarding`, payload);
  }

  // --- Tag APIs ---
  getTags(clientId?: any): Observable<any> {
    if (clientId) {
      return this.http.get<any>(`${this.apiURL}/tags/client/${clientId}`);
    }
    return this.http.get<any>(`${this.apiURL}/tags/`);
  }

  getTagById(id: any): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/tags/${id}`);
  }

  createTag(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/tags/`, payload);
  }

  updateTagDetails(id: any, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiURL}/tags/${id}`, payload);
  }

  deleteTagById(id: any): Observable<any> {
    return this.http.delete<any>(`${this.apiURL}/tags/${id}`);
  }

  updateSessionTags(sessionId: any, tags: number[]): Observable<any> {
    return this.http.patch<any>(`${this.apiURL}/chat/session_data/${sessionId}/tags`, { tags });
  }

  syncWhatsAppTags(clientId: any): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/tags/sync-whatsapp/${clientId}`, {});
  }

  clearHistoryDumps(options?: { client_id?: number | string; org_wa_id?: number | string }): Observable<any> {
    let params = new HttpParams();
    if (options?.client_id !== undefined && options?.client_id !== null && options?.client_id !== '') {
      params = params.set('client_id', options.client_id.toString());
    }
    if (options?.org_wa_id !== undefined && options?.org_wa_id !== null && options?.org_wa_id !== '') {
      params = params.set('org_wa_id', options.org_wa_id.toString());
    }
    return this.http.delete<any>(`${this.apiURL}/webhook/history_dumps/clear`, { params });
  }

  // Organization API Keys (Lead Automation)
  getOrganizationApiKeys(orgId: any): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/organizations/${orgId}/api-keys`);
  }

  createOrganizationApiKey(orgId: any, data: any = {}): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/organizations/${orgId}/api-keys`, data);
  }

  revokeOrganizationApiKey(orgId: any, keyId: any): Observable<any> {
    return this.http.delete<any>(`${this.apiURL}/api/organizations/${orgId}/api-keys/${keyId}`);
  }

  // Client Archive APIs
  archiveClient(clientId: any): Observable<any> {
    return this.http.patch<any>(`${this.apiURL}/api/clients/${clientId}/archive`, {});
  }

  unarchiveClient(clientId: any): Observable<any> {
    return this.http.patch<any>(`${this.apiURL}/api/clients/${clientId}/unarchive`, {});
  }

  getArchivedClients(): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/clients/archived`);
  }

  // WhatsApp Payment Link & Prepay UPI
  getWhatsAppPaymentLink(clientId: any): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/embedded-signup/payment-link?client_id=${clientId}`);
  }

  // WhatsApp Coexistence Sync & Status
  getCoexistenceStatus(clientId: any): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/embedded-signup/coexistence-status?client_id=${clientId}`);
  }

  triggerCoexistenceSync(clientId: any, force: boolean = false): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/embedded-signup/trigger-coexistence-sync`, {
      client_id: clientId,
      force: force
    });
  }

  refreshEmbeddedSignup(): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/embedded-signup/refresh`, {});
  }

  // Organization Master Switch & Addon Configurations APIs
  getMasterSwitchStatus(orgId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/organizations/${orgId}/addons/master-switch`);
  }

  updateMasterSwitchStatus(orgId: number | string, enabled: boolean): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/organizations/${orgId}/addons/master-switch`, { enabled });
  }

  putMasterSwitchStatus(orgId: number | string, enabled: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiURL}/api/organizations/${orgId}/addons/master-switch`, { enabled });
  }

  listAddonConfigs(orgId: number | string, clientId?: number | string): Observable<any[]> {
    let url = `${this.apiURL}/api/organizations/${orgId}/addon-configs`;
    if (clientId) {
      url += `?client_id=${clientId}`;
    }
    return this.http.get<any[]>(url);
  }

  getAddonConfigById(orgId: number | string, configId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/organizations/${orgId}/addon-configs/${configId}`);
  }

  createAddonConfig(orgId: number | string, data: any): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/organizations/${orgId}/addon-configs`, data);
  }

  updateAddonConfig(orgId: number | string, configId: number | string, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiURL}/api/organizations/${orgId}/addon-configs/${configId}`, data);
  }

  toggleAddonConfig(orgId: number | string, configId: number | string, isActive: boolean): Observable<any> {
    return this.http.patch<any>(`${this.apiURL}/api/organizations/${orgId}/addon-configs/${configId}/toggle?is_active=${isActive}`, {});
  }

  deleteAddonConfig(orgId: number | string, configId: number | string): Observable<any> {
    return this.http.delete<any>(`${this.apiURL}/api/organizations/${orgId}/addon-configs/${configId}`);
  }

  // Organization-Level Lead Assignment Rules
  listOrgLeadRules(orgId: number | string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiURL}/api/organizations/${orgId}/lead-assignment-rules`);
  }

  createOrgLeadRule(orgId: number | string, data: any): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/organizations/${orgId}/lead-assignment-rules`, data);
  }

  updateOrgLeadRule(orgId: number | string, ruleId: number | string, data: any): Observable<any> {
    return this.http.put<any>(`${this.apiURL}/api/organizations/${orgId}/lead-assignment-rules/${ruleId}`, data);
  }

  deleteOrgLeadRule(orgId: number | string, ruleId: number | string): Observable<any> {
    return this.http.delete<any>(`${this.apiURL}/api/organizations/${orgId}/lead-assignment-rules/${ruleId}`);
  }

  // Portal sync triggers
  triggerPortalSync(portalName: string = '99acres', configName: string = 'default'): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/leads/sync/${portalName}?config_name=${configName}`, {});
  }

  // ================= Meta Lead Ads 1-Click OAuth Integration =================
  getMetaAuthUrl(clientId: number | string, redirectUri?: string): Observable<any> {
    let url = `${this.apiURL}/api/leads/meta-auth/url?client_id=${clientId}`;
    if (redirectUri) {
      url += `&redirect_uri=${encodeURIComponent(redirectUri)}`;
    }
    return this.http.get<any>(url);
  }

  exchangeMetaToken(payload: { client_id: number | string; access_token?: string; code?: string | null; redirect_uri?: string | null }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/leads/meta-auth/exchange-token`, payload);
  }

  getMetaForms(clientId: number | string, pageId: string): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/leads/meta-auth/forms/${clientId}/${pageId}`);
  }

  subscribeMetaPage(payload: {
    client_id: number | string;
    page_id: string;
    template_name?: string;
    start_conversation?: boolean;
    delay_seconds?: number;
    field_mappings?: Record<string, string>;
  }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/leads/meta-auth/subscribe-page`, payload);
  }

  getMetaStatus(clientId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiURL}/api/leads/meta-auth/status/${clientId}`);
  }

  disconnectMetaPage(payload: { client_id: number | string; page_id?: string }): Observable<any> {
    return this.http.post<any>(`${this.apiURL}/api/leads/meta-auth/disconnect`, payload);
  }
}

